import {Component, computed, DestroyRef, effect, signal, Signal, untracked, viewChildren} from '@angular/core';
import {Location} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ContextService} from '../../services/context.service';
import {Organization} from '../../types-repository';
import {BreadCrumb, LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {CrdLocalClipboard, CrdStreamComponent} from '../crd-stream/crd-stream.component';
import {CrdScreenWindows, CrdWindowMessage} from './crd-screen-windows';
import {CrdDisplayMode} from '../crd-stream/crd-display';
import {CrdDisplaySetting} from '../crd-stream/crd-display-setting';

/**
 * The screens of a host that doesn't say how many it has: one set up before Setup recorded them (the CrdScreens tag,
 * DesktopHost.screens), or an old link without `screens`. Three, the fixed tabs from before the tag: a tab without a
 * display behind it fails its join (the binary stops on a monitor that doesn't exist) rather than show a wrong screen.
 */
export const CRD_SCREENS_FALLBACK = [0, 1, 2];

/** The most screens a link may name; a larger count is no host's (a mangled link) and falls back like any other. */
export const CRD_SCREENS_MAX = 8;

/**
 * The monitors a CRD host streams, 0-based (Join takes the index): `screens` in the link, the host's screen count
 * from the host list (the Connect link carries it; the pop-out links pass it on), else CRD_SCREENS_FALLBACK. At most
 * CRD_SCREENS_MAX: the count comes from a URL, and each screen is a tab and a stream.
 */
export function crdScreens(screens: string | null): number[] {
  const count = Number(screens);
  return screens && Number.isInteger(count) && count >= 1 && count <= CRD_SCREENS_MAX
    ? Array.from({length: count}, (_, i) => i)
    : CRD_SCREENS_FALLBACK;
}

/**
 * How long the session page waits after a screen's own window went away before taking the screen back: a
 * reload of that window releases and claims it again within this time, and the page must not join it meanwhile.
 */
export const CRD_RETURN_GRACE_MS = 3000;

/**
 * The remote desktop of one host, one tab per screen.
 *
 * With no `monitor` query parameter (the host list's Connect link) no screen is selected and nothing joins
 * until a tab or a tab's pop-out icon is chosen. A `monitor` in the link (a reload, an old per-screen link)
 * opens that screen. A screen joins the first time its tab is opened. An opened screen stays connected while
 * hidden, so switching back is instant. Leaving the page destroys the streams, which disconnects them.
 *
 * A screen can be moved to a window of its own (for another monitor of the workstation): the same page
 * with `popout=1` shows only that screen. Its tab here then shows where it went, and gets the screen back
 * when that window closes. Audio stays on this page while it holds a screen; otherwise the window of the lowest
 * moved screen plays it (CrdScreenWindows.audioWindow).
 */
@Component({
  selector: 'app-crd-session',
  templateUrl: './crd-session.component.html',
  styleUrls: ['./crd-session.component.scss'],
  imports: [LogoAreaComponent, HeadlineSectionComponent, CrdStreamComponent],
  providers: [CrdLocalClipboard, CrdScreenWindows],
})
export class CrdSessionComponent {
  readonly screens: number[];
  readonly pool: string;
  readonly hostId: string;
  readonly hostName: string;
  readonly region: string;
  /** This page is the window of one screen, not the tabs. */
  readonly popout: boolean;

  /** The visible screen; none until one is chosen. In a popout, always its screen. */
  active = signal<number | null>(null);
  /** Screens whose tab has been opened: their streams exist (and stay) from then on. */
  opened = signal<number[]>([]);
  /** Screens shown in a window of their own: this page holds no stream for them. */
  moved = signal<number[]>([]);
  /**
   * The screen whose stream plays audio here: the visible one, else another this page holds.
   *
   * Only pop-outs defer to the session pages; two session pages of one host that both hold a screen both play it
   * (doubled audio). Not handled: several windows of one host are what pop-outs are for, and two session pages
   * compete anyway (joining the same screen in both kills one's stream). Before the tabs, two pages did the same.
   */
  audible = computed(() => {
    if (this.popout) {
      this.windowsChanged();
      return this.windows.audioWindow(this.screens) === this.active() ? this.active() : null;
    }
    const own = this.opened().filter(m => !this.moved().includes(m));
    const active = this.active();
    return active !== null && own.includes(active) ? active : (own[0] ?? null);
  });
  /** Bumped on every message from another window: what they keep in storage may have changed. */
  private windowsChanged = signal(0);
  /** In a popout: the main page took the screen back, and this window could not close itself. */
  returned = signal(false);
  /** The browser refused to open a window. */
  popupBlocked = signal(false);
  /** From the most recent successful join, in any window of this host: the Windows password of the host session (one per session, not per join; a new session after Stop, auto-stop or Setup has a new one). */
  password = signal<string | null>(null);
  showPassword = signal(false);

  /** The display modes, as the actions offer them. */
  readonly displayModes: {mode: CrdDisplayMode; label: string; title: string}[] = [
    {mode: 'follow', label: 'Follow window', title: 'The host\'s screen takes the size of this window'},
    {mode: 'actual', label: 'Actual size', title: 'The host keeps its screen size, shown 1:1 (scroll bars when larger than the window)'},
    {mode: 'fit', label: 'Scale to window', title: 'The host keeps its screen size, scaled to this window'},
  ];
  readonly displayMode: Signal<CrdDisplayMode>;

  streams = viewChildren(CrdStreamComponent);
  activeStream = computed(() => this.streams().find(s => s.monitor() === this.active()));

  organization: Signal<Organization>;
  breadCrumbs: Signal<BreadCrumb[]>;

  private returnTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private localClipboard: CrdLocalClipboard;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private contextService: ContextService,
    private windows: CrdScreenWindows,
    private displaySetting: CrdDisplaySetting,
    localClipboard: CrdLocalClipboard,
    destroyRef: DestroyRef,
  ) {
    this.organization = this.contextService.organization;
    this.displayMode = displaySetting.mode;
    this.breadCrumbs = computed(() => [
      {display: 'Content Platform', commands: ['/o', this.organization()?.id]},
      {display: 'Remote Desktop', commands: ['/o', this.organization()?.id, 'crd']},
    ]);
    this.pool = this.route.snapshot.paramMap.get('pool') || '';
    this.hostId = this.route.snapshot.paramMap.get('hostId') || '';
    const q = this.route.snapshot.queryParamMap;
    this.region = q.get('region') || '';
    this.hostName = q.get('hostName') || this.hostId;
    this.popout = q.get('popout') === '1';
    this.screens = crdScreens(q.get('screens'));
    // A monitor that doesn't exist counts as none; a popout always shows one (Screen 1 by default).
    const requested = parseInt(q.get('monitor') ?? '', 10);
    const named = this.screens.includes(requested) ? requested : null;
    const monitor = this.popout ? (named ?? 0) : named;

    this.windows.connect(this.hostId, message => this.onWindowMessage(message));
    localClipboard.onSent = text => this.windows.post({type: 'clipboard', text});
    this.localClipboard = localClipboard;
    if (this.popout) {
      const own = monitor!;
      this.claim(own);
      const release = () => this.release(own);
      window.addEventListener('pagehide', release);
      destroyRef.onDestroy(() => {
        window.removeEventListener('pagehide', release);
        release();
      });
    } else {
      this.moved.set(this.windows.claimed(this.screens));
      // Every stream carries the same system audio: exactly one window plays it. This page does while it holds
      // a screen; the windows check again on the message.
      effect(() => {
        const playing = this.audible() !== null;
        untracked(() => this.announceAudio(playing));
      });
      // Closing this page leaves the audio to a moved screen's window.
      const handOver = () => this.announceAudio(false);
      window.addEventListener('pagehide', handOver);
      destroyRef.onDestroy(() => {
        window.removeEventListener('pagehide', handOver);
        this.returnTimers.forEach(t => clearTimeout(t));
        handOver();
      });
    }
    // After the teardown above (DestroyRef callbacks run in registration order): its messages need the channel.
    destroyRef.onDestroy(() => this.windows.disconnect());
    if (monitor !== null) this.show(monitor);
  }

  /** A tab click: shows the screen and keeps it in the URL, so a reload comes back to it. */
  select(monitor: number) {
    this.show(monitor);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {monitor},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Shows a screen, joining it on its first opening, unless a window of its own holds it. */
  private show(monitor: number) {
    this.active.set(monitor);
    if (!this.opened().includes(monitor) && !this.moved().includes(monitor)) {
      this.opened.update(o => [...o, monitor]);
    }
  }

  /**
   * Opens a screen in a new browser window, which joins it. This page lets go of it first (its stream here, if it
   * had one, would be killed by that join anyway). A screen not opened here yet is never joined here.
   */
  openInWindow(monitor: number) {
    const tree = this.router.createUrlTree(
      ['/o', this.organization()?.id, 'crd', 'view', this.pool, this.hostId],
      {queryParams: {hostName: this.hostName, region: this.region, screens: this.route.snapshot.queryParamMap.get('screens'), monitor, popout: 1}},
    );
    const url = this.location.prepareExternalUrl(this.router.serializeUrl(tree));
    const opened = window.open(url, `crd-${this.hostId}-screen-${monitor + 1}`, 'popup,width=1280,height=800');
    this.popupBlocked.set(!opened);
    if (opened) this.letGo(monitor);
  }

  /** Takes a screen back from its window, which is told to close. */
  bringBack(monitor: number) {
    this.windows.post({type: 'return', monitor});
    this.windows.setClaimed(monitor, false);
    this.takeBack(monitor);
  }

  onJoined(password: string) {
    this.password.set(password || null);
    if (password) this.windows.post({type: 'password', password});
  }

  toggleFullscreen() {
    this.activeStream()?.toggleFullscreen();
  }

  setDisplayMode(mode: CrdDisplayMode) {
    this.displaySetting.set(mode);
  }

  private onWindowMessage(message: CrdWindowMessage) {
    this.windowsChanged.update(n => n + 1);
    if (message.type === 'clipboard') {
      this.localClipboard.last = message.text;
    } else if (message.type === 'password') {
      this.password.set(message.password);
    } else if (this.popout) {
      if (message.type === 'return' && message.monitor === this.active()) {
        this.opened.set([]);
        this.returned.set(true);
        window.close();
      }
    } else if (message.type === 'claimed') {
      this.letGo(message.monitor);
    } else if (message.type === 'released') {
      // A reload of that window claims it again shortly; joining it here meanwhile would kill its stream.
      clearTimeout(this.returnTimers.get(message.monitor));
      this.returnTimers.set(message.monitor, setTimeout(() => {
        this.returnTimers.delete(message.monitor);
        if (!this.windows.claimed([message.monitor]).length) this.takeBack(message.monitor);
      }, CRD_RETURN_GRACE_MS));
    }
  }

  /** This page drops its stream of a screen another window now holds. */
  private letGo(monitor: number) {
    clearTimeout(this.returnTimers.get(monitor));
    this.returnTimers.delete(monitor);
    if (!this.moved().includes(monitor)) this.moved.update(m => [...m, monitor]);
    this.opened.update(o => o.filter(m => m !== monitor));
  }

  private takeBack(monitor: number) {
    this.moved.update(m => m.filter(x => x !== monitor));
    if (this.active() === monitor) this.show(monitor);
  }

  private announceAudio(playing: boolean) {
    this.windows.setPageHasAudio(playing);
    this.windows.post({type: 'audio'});
  }

  private claim(monitor: number) {
    this.windows.setClaimed(monitor, true);
    this.windows.post({type: 'claimed', monitor});
  }

  private release(monitor: number) {
    if (this.returned()) return;
    this.windows.setClaimed(monitor, false);
    this.windows.post({type: 'released', monitor});
  }
}
